// src/utils/generateDiploma.js
import { jsPDF } from 'jspdf';

let LOGO_ESCUDO = null;
let LOGO_TEXTO  = null;
try {
  const logos = require('./diplomaLogos');
  LOGO_ESCUDO = logos.LOGO_ESCUDO;
  LOGO_TEXTO  = logos.LOGO_TEXTO;
} catch (e) {}

const LEVEL_DESCRIPTIONS = {
  A1: 'PRINCIPIANTE', A2: 'BASICO',
  B1: 'INTERMEDIO',   B2: 'INTERMEDIO ALTO',
  C1: 'AVANZADO',     C2: 'MAESTRIA',
};

const SKILL_COLORS = {
  grammar:    { r: 41,  g: 98,  b: 197 },
  vocabulary: { r: 196, g: 48,  b: 43  },
  reading:    { r: 39,  g: 153, b: 74  },
   listening:  { r: 139, g: 92,  b: 246 },
};

const SKILL_LABELS = {
  grammar: 'Gramatica', vocabulary: 'Vocabulario', reading: 'Reading', listening: 'Listening',  
};

const drawBorder = (doc, W, H) => {
  doc.setDrawColor(25, 70, 140);
  doc.setLineWidth(4);
  doc.rect(5, 5, W - 10, H - 10);

  doc.setLineWidth(0.8);
  doc.setDrawColor(35, 85, 160);
  doc.rect(9, 9, W - 18, H - 18);

  doc.setLineWidth(0.3);
  doc.setDrawColor(50, 100, 180);
  doc.rect(11, 11, W - 22, H - 22);

  const size = 10;
  [[5,5],[W-15,5],[5,H-15],[W-15,H-15]].forEach(([x, y]) => {
    doc.setFillColor(25, 70, 140);
    doc.rect(x, y, size, size, 'F');
    doc.setFillColor(253, 251, 244);
    doc.rect(x + 2, y + 2, size - 4, size - 4, 'F');
    doc.setFillColor(25, 70, 140);
    doc.circle(x + size / 2, y + size / 2, 1.5, 'F');
  });

  doc.setDrawColor(160, 40, 40);
  doc.setLineWidth(0.8);
  doc.line(18, 58, W - 18, 58);
  doc.line(18, 60, W - 18, 60);
  doc.line(18, H - 58, W - 18, H - 58);
  doc.line(18, H - 60, W - 18, H - 60);
};

export const generateDiploma = (userName, finalLevel, skillScores) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // ✅ Helper: nunca pasa undefined a jsPDF
  const t = (text, x, y, opts = {}) => {
    const str = (text === null || text === undefined) ? '' : String(text);
    if (str === '') return;
    doc.text(str, x, y, opts);
  };

  // ✅ Datos seguros desde el inicio
  const safeName   = String(userName   || 'Estudiante').trim();
  const safeLevel  = String(finalLevel || 'A1').trim();
  const safeDesc   = String(LEVEL_DESCRIPTIONS[safeLevel] || '');
  const safeScores = {
    grammar:    Number(skillScores?.grammar    ?? 0),
    vocabulary: Number(skillScores?.vocabulary ?? 0),
    reading:    Number(skillScores?.reading    ?? 0),
     listening:  Number(skillScores?.listening  ?? 0), 
  };

  const W = 210, H = 297, cx = W / 2;

  // Fondo crema
  doc.setFillColor(253, 251, 244);
  doc.rect(0, 0, W, H, 'F');

  drawBorder(doc, W, H);

  // Logos
  if (LOGO_ESCUDO) {
    try { doc.addImage(LOGO_ESCUDO, 'JPEG', 18, 14, 34, 34); } catch (e) {}
  }
  if (LOGO_TEXTO) {
    try { doc.addImage(LOGO_TEXTO, 'JPEG', W - 52, 14, 34, 34); } catch (e) {}
  }

  // Título
  doc.setTextColor(25, 55, 120);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  t('CERTIFICADO DE NIVEL', cx, 38, { align: 'center' });

  // Banda Master Challenge
  doc.setFillColor(170, 35, 35);
  doc.rect(cx - 42, 42, 84, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  t('- MASTER CHALLENGE -', cx, 47.5, { align: 'center' });

  // Otorgado a
  doc.setTextColor(90, 90, 90);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'italic');
  t('Otorgado a', cx, 75, { align: 'center' });

  // Nombre
  doc.setTextColor(15, 15, 55);
  doc.setFontSize(28);
  doc.setFont('times', 'bolditalic');
  t(safeName, cx, 93, { align: 'center' });

  // Línea bajo el nombre
  doc.setDrawColor(25, 70, 140);
  doc.setLineWidth(0.8);
  const nameW = Math.min(doc.getTextWidth(safeName) + 20, 150);
  doc.line(cx - nameW / 2, 97, cx + nameW / 2, 97);

  // Subtítulo
  doc.setTextColor(90, 90, 90);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'italic');
  t('Ha alcanzado el siguiente nivel de competencia:', cx, 108, { align: 'center' });

  // Caja nivel
  doc.setFillColor(235, 240, 255);
  doc.setDrawColor(25, 55, 120);
  doc.setLineWidth(0.6);
  doc.roundedRect(cx - 28, 113, 56, 18, 3, 3, 'FD');

  doc.setTextColor(25, 55, 120);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  t('NIVEL:', cx - 8, 124, { align: 'right' });

  doc.setFontSize(22);
  t(safeLevel, cx + 10, 125, { align: 'center' }); // ✅ siempre string

  // Descripción del nivel
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  t(safeDesc, cx, 136, { align: 'center' }); // ✅ siempre string

  // Escala A1-C2
  const levels = ['A1','A2','B1','B2','C1','C2'];
  const cellW = 22, scaleX = cx - (levels.length * cellW) / 2, scaleY = 141;

  levels.forEach((lvl, i) => {
    const x        = scaleX + i * cellW;
    const isActive = lvl === safeLevel;
    doc.setFillColor(isActive?25:225, isActive?55:230, isActive?120:245);
    doc.roundedRect(x + 0.5, scaleY, cellW - 1, 9, 2, 2, 'F');
    doc.setDrawColor(isActive?25:180, isActive?55:190, isActive?120:210);
    doc.setLineWidth(0.4);
    doc.roundedRect(x + 0.5, scaleY, cellW - 1, 9, 2, 2, 'S');
    doc.setFontSize(isActive ? 9 : 7.5);
    doc.setFont('helvetica', isActive ? 'bold' : 'normal');
    doc.setTextColor(isActive?255:80, isActive?255:90, isActive?255:120);
    t(lvl, x + cellW / 2, scaleY + 6, { align: 'center' });
  });

  // Separador resultados
  doc.setDrawColor(25, 70, 140);
  doc.setLineWidth(0.5);
  doc.line(30, 158, cx - 8, 158);
  doc.line(cx + 8, 158, W - 30, 158);
  doc.setTextColor(50, 50, 50);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  t('RESULTADOS OBTENIDOS:', cx, 163, { align: 'center' });
  doc.line(30, 166, cx - 8, 166);
  doc.line(cx + 8, 166, W - 30, 166);

  // Barras por skill
  const barStartX = 32, barMaxW = 118, barH = 6;
  let barY = 176;

  Object.entries(safeScores).forEach(([skill, score]) => {
    const color  = SKILL_COLORS[skill] || { r: 100, g: 100, b: 200 };
    const label  = SKILL_LABELS[skill]  || skill;
    const filled = (barMaxW * score) / 100;

    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    t(`${label}:`, barStartX + 26, barY + 4.5, { align: 'right' });

    doc.setFillColor(215, 215, 215);
    doc.roundedRect(barStartX + 28, barY, barMaxW, barH, 2, 2, 'F');

    doc.setFillColor(color.r, color.g, color.b);
    if (filled > 2) {
      doc.roundedRect(barStartX + 28, barY, filled, barH, 2, 2, 'F');
    }

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(color.r, color.g, color.b);
    doc.setFontSize(10);
    t(`${score}%`, barStartX + 28 + barMaxW + 6, barY + 4.5); // ✅ score es Number

    barY += 14;
  });

  // Zona de firma
  const sigY = 238;
  doc.setTextColor(30, 40, 110);
  doc.setFontSize(18);
  doc.setFont('times', 'italic');
  t('EducaT Mexico', 58, sigY - 4, { align: 'center' });

  doc.setDrawColor(100, 100, 130);
  doc.setLineWidth(0.5);
  doc.line(28, sigY + 1, 88, sigY + 1);

  doc.setTextColor(80, 80, 80);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  t('Centro de Idiomas', 58, sigY + 6,  { align: 'center' });
  t('Montreal',          58, sigY + 10, { align: 'center' });

 

  // Fecha
  doc.setTextColor(120, 120, 120);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'italic');
  const fecha = new Date().toLocaleDateString('es-MX', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
  t(`Montreal, ${fecha}`, cx, H - 18, { align: 'center' });

  // Guardar
  const fileNameSafe = safeName.replace(/\s+/g, '_');
  doc.save(`Diploma_${safeLevel}_${fileNameSafe}.pdf`);
};
